/**
 * Affilka, as exposed today under the `affilka` id in backoffice/Telegram, both
 * of which route it through `/postbacks/affise` (generic `PostbackQueryDto`).
 * They alias Affilka's own macros into OUR generic `subid=`/`uid=`/
 * `tracking_tag=` keys so the generic DTO reads them correctly; the extra
 * `campaign_id=`/`player_id=`/`promo_id=`/`visit_id=` copies in the REG
 * template and the CPA-only `campaignId`/`offerId`/`transactionId`/`sub1`/
 * `sub2`/`sub3` params are inert on this route (PostbackQueryDto does not
 * declare them, whitelist:true drops them).
 *
 * TMA instead routes its "Affilka" entry through a DEDICATED `/postbacks/affilka`
 * route (`AffilkaPostbackDto` + `mapAffilka`), sending the raw
 * `campaign_id=`/`player_id=`/`tracking_tag=`/`amount=`/`currency=` keys
 * directly, without our generic `subid=`/`uid=`. That also works — on its own
 * endpoint — but it is a different route entirely from what this contract
 * declares. See the drift table: this is an endpoint-routing divergence, not a
 * macro-casing one, and it is left as an open question which route should be
 * canonical going forward.
 */
export const affilkaContract = {
    id: 'affilka',
    displayName: 'Affilka',
    route: 'affise',
    trackingTagMacro: '${visit_id}',
    amountBehavior: 'FTD amount from ${amount}. QUALIFIED_CPA amount from ${effective_deposits_sum} — a different macro, ' +
        'not the same field as FTD amount. currency is read directly (${currency}) via the generic DTO.',
    duplicateParamPolicy: 'array-then-tolerant-last-wins',
    caseSensitiveParams: [],
    pitfalls: [
        'Routes through the generic /postbacks/affise endpoint, not a dedicated Affilka endpoint, even ' +
            'though a dedicated /postbacks/affilka (AffilkaPostbackDto) exists server-side and TMA already uses it.',
        'CPA extras (campaignId, offerId, transactionId, sub1, sub2, sub3) are decorative on this route: ' +
            'PostbackQueryDto does not declare them and they are silently dropped.',
    ],
    sourceRefs: [
        'apps/api/src/modules/postbacks/controllers/postbacks.controller.ts:145-178 (route used by backoffice/Telegram)',
        'apps/api/src/modules/postbacks/controllers/postbacks.controller.ts:299-341 (dedicated /postbacks/affilka, used only by TMA)',
        'apps/api/src/modules/postbacks/services/parameter-mapper.service.ts:33-57 (mapAffilka)',
    ],
    events: [
        {
            event: 'REG',
            eventValue: 'registration',
            params: [
                { ourParam: 'subid', macro: '${campaign_id}' },
                { ourParam: 'uid', macro: '${player_id}' },
                { ourParam: 'promo_id', macro: '${promo_id}' },
                { ourParam: 'visit_id', macro: '${visit_id}' },
                { ourParam: 'campaign_id', macro: '${campaign_id}' },
                { ourParam: 'player_id', macro: '${player_id}' },
                { ourParam: 'tracking_tag', macro: '${visit_id}' },
            ],
        },
        {
            event: 'FTD',
            eventValue: 'ftd',
            params: [
                { ourParam: 'subid', macro: '${campaign_id}' },
                { ourParam: 'uid', macro: '${player_id}' },
                { ourParam: 'amount', macro: '${amount}' },
                { ourParam: 'currency', macro: '${currency}' },
                { ourParam: 'visit_id', macro: '${visit_id}' },
                { ourParam: 'deposit_id', macro: '${deposit_id}' },
                { ourParam: 'promo_id', macro: '${promo_id}' },
                { ourParam: 'campaign_id', macro: '${campaign_id}' },
                { ourParam: 'player_id', macro: '${player_id}' },
                { ourParam: 'tracking_tag', macro: '${visit_id}' },
            ],
        },
        {
            event: 'QUALIFIED_CPA',
            eventValue: 'cpa',
            params: [
                { ourParam: 'uid', macro: '${player_id}' },
                { ourParam: 'amount', macro: '${effective_deposits_sum}' },
                { ourParam: 'currency', macro: '${currency}' },
                { ourParam: 'campaignId', macro: '${campaign_id}' },
                { ourParam: 'offerId', macro: '${promo_id}' },
                { ourParam: 'transactionId', macro: '${visit_id}' },
                { ourParam: 'sub1', macro: '${deposits_count}' },
                { ourParam: 'sub2', macro: '${wager}' },
                { ourParam: 'sub3', macro: '${first_deposit}' },
                { ourParam: 'tracking_tag', macro: '${visit_id}' },
            ],
        },
    ],
};
/**
 * TMA's "Affilka" entry, byte-for-byte: routes through the dedicated
 * `/postbacks/affilka` endpoint (`AffilkaPostbackDto` + `mapAffilka`), sending
 * Affilka's own `campaign_id=`/`player_id=` keys directly — no generic
 * `subid=`/`uid=` aliasing. ADDITIVE: `affilkaContract` above (route
 * `/postbacks/affise`, backoffice/Telegram's choice) is unchanged; this
 * contract exists so a migration of the TMA surface can reproduce ITS current
 * output exactly. See drift table row 3 — which endpoint should be canonical
 * for Affilka going forward is an open question, not decided by adding this.
 */
export const affilkaNativeContract = {
    id: 'affilka-native',
    displayName: 'Affilka (native /postbacks/affilka — TMA current output)',
    route: 'affilka',
    trackingTagMacro: '${visit_id}',
    amountBehavior: affilkaContract.amountBehavior,
    duplicateParamPolicy: 'array-then-tolerant-last-wins',
    caseSensitiveParams: [],
    pitfalls: [
        "This is TMA's current output on the DEDICATED /postbacks/affilka endpoint, distinct from " +
            "`affilkaContract` (route /postbacks/affise, backoffice/Telegram's current output). Both are " +
            'real, both work on their own route — see the drift table for the open question of which should ' +
            'be canonical.',
    ],
    sourceRefs: [
        'apps/tma-analytics/src/pages/LinksPage.tsx (PLATFORM_TEMPLATES["/postbacks/affilka"])',
        'apps/api/src/modules/postbacks/controllers/postbacks.controller.ts:299-341 (dedicated /postbacks/affilka)',
        'apps/api/src/modules/postbacks/services/parameter-mapper.service.ts:33-57 (mapAffilka)',
    ],
    events: [
        {
            event: 'REG',
            eventValue: 'registration',
            params: [
                { ourParam: 'campaign_id', macro: '${campaign_id}' },
                { ourParam: 'player_id', macro: '${player_id}' },
                { ourParam: 'promo_id', macro: '${promo_id}' },
                { ourParam: 'visit_id', macro: '${visit_id}' },
                { ourParam: 'tracking_tag', macro: '${visit_id}' },
            ],
        },
        {
            event: 'FTD',
            eventValue: 'ftd',
            params: [
                { ourParam: 'campaign_id', macro: '${campaign_id}' },
                { ourParam: 'player_id', macro: '${player_id}' },
                { ourParam: 'amount', macro: '${amount}' },
                { ourParam: 'currency', macro: '${currency}' },
                { ourParam: 'visit_id', macro: '${visit_id}' },
                { ourParam: 'deposit_id', macro: '${deposit_id}' },
                { ourParam: 'promo_id', macro: '${promo_id}' },
                { ourParam: 'tracking_tag', macro: '${visit_id}' },
            ],
        },
        {
            event: 'QUALIFIED_CPA',
            eventValue: 'cpa',
            params: [
                { ourParam: 'player_id', macro: '${player_id}' },
                { ourParam: 'amount', macro: '${effective_deposits_sum}' },
                { ourParam: 'currency', macro: '${currency}' },
                { ourParam: 'campaign_id', macro: '${campaign_id}' },
                { ourParam: 'promo_id', macro: '${promo_id}' },
                { ourParam: 'visit_id', macro: '${visit_id}' },
                { ourParam: 'tracking_tag', macro: '${visit_id}' },
            ],
        },
    ],
};
//# sourceMappingURL=affilka.js.map